import { useIntl } from '@weenat/wintl';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { useClient } from '../../../ClientProvider';
import useConvertedValue from '../../../hooks/useConvertedValue';
import useQuery from '../../../useQuery';
const metric = 'T';
const DEFAULT_MAX_Y = 1750;
export const useMaizyChart = ({ plot, before, since }) => {
    const { timezone } = plot;
    const client = useClient();
    const { t } = useIntl();
    const { getUnit, convertValue } = useConvertedValue();
    const unit = getUnit(metric);
    const cumulativeHistoryRequest = useQuery(client.cumulativeSum.history({ plotId: plot.id, before, since }), { enabled: !isNil(since) && !isNil(before) });
    const datas = useMemo(() => {
        const measuredData = !isNil(cumulativeHistoryRequest.data)
            ? Object.entries(cumulativeHistoryRequest.data.cumulativeSum || {}).map(([timestamp, value]) => ({
                x: moment.unix(parseInt(timestamp, 10)).tz(timezone).toDate(),
                y: value,
                type: 'measured'
            }))
            : [];
        const referenceData = !isNil(cumulativeHistoryRequest.data)
            ? Object.entries(cumulativeHistoryRequest.data.referenceCumulativeSum || {}).map(([timestamp, value]) => ({
                x: moment.unix(parseInt(timestamp, 10)).tz(timezone).toDate(),
                y: value,
                type: 'normal'
            }))
            : [];
        let maxY = Math.max(...measuredData.filter((m) => m !== null).map((m) => m.y), ...referenceData.filter((m) => m !== null).map((m) => m.y));
        if (!Number.isFinite(maxY)) {
            maxY = DEFAULT_MAX_Y;
        }
        return { measuredData, referenceData, maxY };
    }, [cumulativeHistoryRequest.data]);
    const typeOfDegrees = `(${t(`units.${unit}_abbr`)})`;
    const labels = useCallback(({ datum }) => {
        const typeLabel = t(`dss.maizy.legends.chart.${datum.type}`);
        return `${typeLabel}:${t('dss.maizy.legends.chart.valueFormatted', {
            value: convertValue({ metric, value: datum.y, displayUnit: false }),
            unit: typeOfDegrees
        })}`;
    }, [typeOfDegrees]);
    const axisLabel = `${t('dss.maizy.legends.chart.ordinateTitle', {
        unit: typeOfDegrees
    })}`;
    const yAxisFormat = useCallback((t) => convertValue({ metric, value: t, displayUnit: false }), [convertValue]);
    const xAxisFormat = useCallback((tickValue) => {
        return moment(tickValue).tz(timezone).format('DD MMM');
    }, [timezone]);
    const hasNoData = isEmpty(datas.measuredData) && isEmpty(datas.referenceData);
    return {
        yAxisFormat,
        xAxisFormat,
        axisLabel,
        labels,
        ...datas,
        isLoading: cumulativeHistoryRequest.isLoading,
        hasNoData
    };
};
